import { useMutation } from "@tanstack/react-query";
import { useCallback, useContext } from "react";

import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import {
	createLtlMutationErrorHandler,
	getLtlFallbackMessage,
} from "~/features/quote/utils/ltl-error-handler.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import type { QuoteResponse } from "~/shared/schemas/quote-schema.ts";
import { quoteResponseSchema } from "~/shared/schemas/quote-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

import type { LtlTenderFlowBody } from "../schemas/ltl-tender-flow-schema.ts";
import { ltlTenderFlowBodySchema } from "../schemas/ltl-tender-flow-schema.ts";
import { applyVendorEstimatedClasses } from "../utils/apply-vendor-estimated-class.ts";
import { formatLtlTenderFlowBody } from "../utils/format-ltl-tender-flow-body.ts";

type LtlTenderFlowResult =
	| { success: true; data: QuoteResponse }
	| { success: false; error: string };

const useLtlTenderFlowMutation = (isInternal: boolean) => {
	return useMutation({
		mutationFn: async (body: LtlTenderFlowBody) => {
			return await postAndValidate(
				isInternal ? apiRoutes.LTLTENDERFLOW : apiRoutes.LTLTENDERFLOWCUSTOMER,
				body,
				quoteResponseSchema,
				{
					onError: createLtlMutationErrorHandler(
						getLtlFallbackMessage(isInternal),
					),
				},
			);
		},
	});
};

export const useLtlTenderFlow = () => {
	const { isInternal } = useDeploymentType();
	const { isVendor } = useIsVendor();
	const { quoteState } = useContext(QuoteDetailsContext);
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { data: pageData } = useQuoteDetailsPageLoadQueries();
	const { mutateAsync, isPending } = useLtlTenderFlowMutation(isInternal);

	const submitLtlTenderFlow =
		useCallback(async (): Promise<LtlTenderFlowResult> => {
			// Vendors may leave the freight class blank; substitute the estimated
			// class (derived from density) before formatting so the backend still
			// receives a freight class for those handling units. When an estimate is
			// substituted, flag it so the backend knows the class wasn't user-chosen.
			const { quoteState: effectiveQuoteState, estimatedClassApplied } =
				isVendor
					? applyVendorEstimatedClasses(
							quoteState,
							pageData?.freightClassData ?? [],
						)
					: { quoteState, estimatedClassApplied: false };
			const body = formatLtlTenderFlowBody(
				effectiveQuoteState,
				selectedAddressBookItems,
				isInternal,
				estimatedClassApplied,
			);
			const parsed = ltlTenderFlowBodySchema.safeParse(body);
			if (!parsed.success) {
				console.error("Error validating LTL tender flow body:", parsed.error);
				return {
					success: false,
					error:
						"We encountered an issue with the shipment information. Please review your details and try again.",
				};
			}
			const result = await mutateAsync(parsed.data);
			if (!result.success) {
				return {
					success: false,
					error: result.error || getLtlFallbackMessage(isInternal),
				};
			}
			return { success: true, data: result.data };
		}, [
			isInternal,
			isVendor,
			mutateAsync,
			pageData?.freightClassData,
			quoteState,
			selectedAddressBookItems,
		]);

	return { submitLtlTenderFlow, isLoading: isPending };
};
